import React, { useMemo } from 'react';
import { CallState, AgentState, SAPContactSearchState, ConferenceState } from '../../types';
import { SapContact } from '../../services/sap-cti.service';
import { CallCard } from '../call/CallCard';
import { ACWTimer } from '../acw/ACWTimer';
import { AcwCallTraceTimeline } from '../acw/AcwCallTraceTimeline';
import acwViewStyles from '../acw/AcwView.module.css';
import { MicrophoneSettings } from '../settings/MicrophoneSettings';
import { ProfileSettings } from '../profile/ProfileSettings';
import { AgentWidgetIdleState } from './AgentWidgetIdleState';
import { getDefaultAcwTimeout } from '../../utils/config';
import { getCallTraceId } from '../../services/call-log/call-log.service';
import { loadAcwPendingContext } from '../../hooks/agent-widget/acw-pending-context';
import { getAcwLockedContactId } from '../../hooks/agent-widget/acw-ui-lock';
import { LastCallInfo, ManualSearchState } from '../../hooks/agent-widget/types';
import { ACWResult } from '../acw/acw.types';
import { CallDisplayContext } from '../call/call-card.types';

interface AgentWidgetContentProps {
  showMicSettings: boolean;
  onCloseMicSettings: () => void;
  showProfile: boolean;
  onCloseProfile: () => void;
  isIdleView: boolean;
  isCallView: boolean;
  showACW: boolean;
  agentState: AgentState;
  callState: CallState;
  conferenceState: ConferenceState;
  isRinging: boolean;
  isMuted: boolean;
  isRecording: boolean;
  isAgentLegActive: boolean;
  callError: string | null;
  isSapC4cEnabled: boolean;
  sapContactState?: SAPContactSearchState;
  manualSearchState?: ManualSearchState;
  openedSapContacts: SapContact[];
  acwTimeout: number | null;
  lastCallInfo: LastCallInfo | null;
  callDisplayContext?: CallDisplayContext;
  onAccept: () => void;
  onReject: () => void;
  onTransfer: (target?: string) => void;
  onConference: () => void;
  onCallEnd: () => void;
  onContactOpened: (contact: SapContact) => void;
  onManualSearch?: (params: { query: string }) => void;
  onManualDial?: (phoneNumber: string) => Promise<void>;
  onAcwComplete: (result: ACWResult) => void | Promise<void>;
}

export function AgentWidgetContent({
  showMicSettings,
  onCloseMicSettings,
  showProfile,
  onCloseProfile,
  isIdleView,
  isCallView,
  showACW,
  agentState,
  callState,
  conferenceState,
  isRinging,
  isMuted,
  isRecording,
  isAgentLegActive,
  callError,
  isSapC4cEnabled,
  sapContactState,
  manualSearchState,
  openedSapContacts,
  acwTimeout,
  lastCallInfo,
  callDisplayContext,
  onAccept,
  onReject,
  onTransfer,
  onConference,
  onCallEnd,
  onContactOpened,
  onManualSearch,
  onManualDial,
  onAcwComplete,
}: AgentWidgetContentProps) {
  const acwContactsForDisplay = useMemo((): SapContact[] => {
    if (openedSapContacts.length > 0) {
      return openedSapContacts;
    }

    const matched = sapContactState?.contacts?.[0];
    if (!matched) {
      return [];
    }

    const name =
      [matched.firstName, matched.lastName].filter(Boolean).join(' ').trim() ||
      matched.accountName ||
      'Unknown Contact';

    return [
      {
        id: matched.id,
        displayId: matched.displayId,
        firstName: matched.firstName,
        name,
        phone: matched.phone,
        // Empty until ACCEPT/link; completeAcw mints a referenceId if still unset.
        referenceId: '',
        type: matched.type,
        accountId: matched.accountId,
      },
    ];
  }, [openedSapContacts, sapContactState?.contacts]);

  const acwSkillId = useMemo(() => {
    const pending = loadAcwPendingContext();
    return lastCallInfo?.skillId || callState.skillId || pending?.skillId || undefined;
  }, [lastCallInfo?.skillId, callState.skillId]);
  const acwContactId = useMemo(() => {
    const pending = loadAcwPendingContext();
    return (
      lastCallInfo?.contactId ||
      getAcwLockedContactId() ||
      callState.contactId ||
      pending?.contactId ||
      undefined
    );
  }, [lastCallInfo?.contactId, callState.contactId]);
  const acwCallTraceId = useMemo(
    () => lastCallInfo?.callTraceId ?? getCallTraceId(),
    [lastCallInfo?.callTraceId]
  );

  return (
    <>
      {showMicSettings ? (
        <MicrophoneSettings onClose={onCloseMicSettings} />
      ) : showProfile ? (
        <ProfileSettings onClose={onCloseProfile} />
      ) : null}

      {isIdleView && <AgentWidgetIdleState agentState={agentState} onManualDial={onManualDial} />}

      {isCallView && (
        <CallCard
          callState={callState}
          conferenceState={conferenceState}
          isRinging={isRinging}
          isMuted={isMuted}
          isRecording={isRecording}
          isAgentLegActive={isAgentLegActive}
          callError={callError}
          callDisplayContext={callDisplayContext}
          sapContactState={isSapC4cEnabled ? sapContactState : undefined}
          manualSearchState={isSapC4cEnabled ? manualSearchState : undefined}
          onAccept={onAccept}
          onReject={onReject}
          onTransfer={onTransfer}
          onConference={onConference}
          onCallEnd={onCallEnd}
          onContactOpened={onContactOpened}
          onManualSearch={isSapC4cEnabled ? onManualSearch : undefined}
          linkedSapContactId={openedSapContacts[0]?.id ?? null}
        />
      )}

      {showACW && !isCallView && !showMicSettings && !showProfile && (
        <div className={acwViewStyles.acwStack}>
          <ACWTimer
            duration={acwTimeout || getDefaultAcwTimeout()}
            skillName={lastCallInfo?.skillName || callState.skillName || undefined}
            skillId={acwSkillId}
            contactId={acwContactId}
            openedSapContacts={acwContactsForDisplay}
            requireDisposition={lastCallInfo?.requireDisposition || callState.requireDisposition}
            onComplete={onAcwComplete}
          />
          <AcwCallTraceTimeline
            callTraceId={acwCallTraceId}
            cxoneContactId={acwContactId}
            fallbackDirection={
              lastCallInfo?.direction ??
              (callDisplayContext?.isOutboundCall ? 'OUTBOUND' : callState.direction)
            }
          />
        </div>
      )}
    </>
  );
}
